import { fetchSkillPathData, fetchCurriculumData } from './dataApi';
import { topicLessonsFromIndex } from './topicLessons';

// Split-file + native/ pattern (see dev/stracture.md's Wordclass Vocabulary
// format, and Conversation's split-file precedent). Each curriculum home's
// grammar curriculum (48 lessons) used to live in one big consolidated
// grammar.json; it's now one small file per lesson, grammar/{lessonId}.json,
// so viewing a single lesson (the common case) doesn't require downloading
// every other lesson's examples/tokens/structure data too. grammar/catalog.json
// is the same index1/index2/... chapter-catalog shape alphabet/topic-lessons
// use (see topicLessonsFromIndex) — a flat, ordered chapter list, no
// title/description/learningPath groupings or per-lesson levelRange/category/
// portability metadata. Unlike vocabulary there is no shared English anchor
// to overlay onto — every home's grammar is genuinely its own (different
// languages have different tenses/moods/article systems), so the target
// content always comes directly from the curriculum's own home.
//
// The "native" (reader-language) side only ever needs a handful of fields —
// concept, learningObjectives, pattern labels, mistake explanations, and
// example translation hints — never the full example/token/structure data,
// which is meaningless outside the target language it describes.
// native/{code}/index.json holds just that reduced
// subset for all 48 lessons in one file (small enough on its own — no need
// to split it too), for all ~40 supported reader languages.
const manifestPromises = new Map();
const sectionPromises = new Map();
const sectionsPromises = new Map();
const nativePromises = new Map();

async function resolveGrammarManifest(homeLang) {
  try {
    const index = await fetchSkillPathData(homeLang, 'grammar/catalog.json');
    return topicLessonsFromIndex(index);
  } catch (error) {
    if (homeLang === 'en') throw error;
    const sourceIndex = await fetchSkillPathData('en', 'grammar/catalog.json');
    const sourceChapters = topicLessonsFromIndex(sourceIndex);
    const probeId = sourceChapters[0]?.id;
    if (!probeId) throw error;
    // Localized lesson batches can arrive before their catalog. Only fall
    // back to the English chapter list after proving lesson content
    // actually exists — probe with the English catalog's own first lesson
    // id, since lesson ids are portable/shared across every locale's
    // curriculum.
    await loadGrammarSection(homeLang, probeId);
    return sourceChapters;
  }
}

export function loadGrammarManifest(homeLang) {
  if (!manifestPromises.has(homeLang)) {
    manifestPromises.set(homeLang, resolveGrammarManifest(homeLang).catch((error) => {
      manifestPromises.delete(homeLang);
      throw error;
    }));
  }
  return manifestPromises.get(homeLang);
}

// Fetches one lesson's own file — the shape (`{ sections: [...] }`) matches
// the old consolidated document so callers that used to find-by-id in the
// full sections array need no changes beyond passing the id up front.
export function loadGrammarSection(homeLang, lessonId) {
  const key = `${homeLang}:${lessonId}`;
  if (!sectionPromises.has(key)) {
    sectionPromises.set(
      key,
      fetchSkillPathData(homeLang, `grammar/${lessonId}.json`).catch((error) => {
        sectionPromises.delete(key);
        throw error;
      }),
    );
  }
  return sectionPromises.get(key);
}

// Reassembles the full per-home section list (in manifest order) from the
// split lesson files, for callers that genuinely need many/all lessons at
// once (e.g. Practice's tense_aspect sampling) rather than a single lesson.
// A lesson file that fails to fetch is skipped rather than failing the
// whole batch.
export function loadGrammarSections(homeLang) {
  if (!sectionsPromises.has(homeLang)) {
    sectionsPromises.set(
      homeLang,
      (async () => {
        const chapters = await loadGrammarManifest(homeLang);
        const docs = await Promise.all(
          chapters.map((chapter) =>
            loadGrammarSection(homeLang, chapter.id).catch(() => null)),
        );
        return { sections: docs.flatMap((doc) => doc?.sections || []) };
      })().catch((error) => {
        sectionsPromises.delete(homeLang);
        throw error;
      }),
    );
  }
  return sectionsPromises.get(homeLang);
}

// grammar/{lessonId}.json's `lesson` field (added alongside the existing
// `sections` field) holds the same numbered-key/`type`-tagged card format
// topic-lessons uses, so TopicLessonPage's card renderer/quiz flow (see
// ReadingMaterial/ConversationMaterial/QuestionCard) can render it directly
// without a bespoke grammar renderer. `sections` (tokens/lexemes/
// grammarTags) is untouched by this and keeps feeding Practice and Search.
export async function loadGrammarLessonCards(homeLang, lessonId) {
  const document = await loadGrammarSection(homeLang, lessonId);
  const sections = document.sections || [];
  const label = sections.find((section) => section.id === lessonId)?.label
    || sections[0]?.label
    || lessonId;
  const chapter = { id: lessonId, label, path: `${lessonId}.json`, type: 'grammar' };
  const lessonCards = document.lesson || {};
  const items = Object.entries(lessonCards)
    .filter(([key, item]) => /^\d+$/.test(key) && item && typeof item === 'object')
    .sort(([first], [second]) => Number(first) - Number(second))
    .map(([, item]) => item);
  return { chapter, chapters: [chapter], items };
}

// fetchCurriculumData (not fetchSkillPathData) — the native/ path has 3+
// segments after the module name, which fetchSkillPathData's bundler routing
// would otherwise misinterpret as {locale}/{relPath} and try a manifest
// lookup for a "native" locale that doesn't exist.
export function loadGrammarNativeOverlay(homeLang, code) {
  const key = `${homeLang}:${code}`;
  if (!nativePromises.has(key)) {
    nativePromises.set(
      key,
      fetchCurriculumData(homeLang, `grammar/native/${code}/index.json`).catch(() => ({ sections: [] })),
    );
  }
  return nativePromises.get(key);
}

export function clearGrammarCache() {
  manifestPromises.clear();
  sectionPromises.clear();
  sectionsPromises.clear();
  nativePromises.clear();
}
